import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TESTIDS } from "@/constants/testIds";
import { ROLES } from "@/constants/roles";
import {
  LogOut, LayoutDashboard, History, Users, FileBarChart2, Settings2, ShieldCheck,
  Building2, PlaneTakeoff, Clock3, CheckSquare, KeyRound, Landmark, Menu, X,
} from "lucide-react";

const linkBase =
  "px-3 py-2 text-sm font-medium hover:bg-black hover:text-white transition-colors border border-transparent whitespace-nowrap";
const activeCls = "bg-black text-white";
const mobileLinkBase =
  "flex items-center gap-3 px-4 py-3 text-base font-medium hover:bg-black hover:text-white transition-colors border-l-4 border-transparent";
const mobileActive = "bg-black text-white border-l-4 border-klein";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "admin_unit";

  const NavItems = ({ mobile = false }) => {
    const base = mobile ? mobileLinkBase : linkBase;
    const active = mobile ? mobileActive : activeCls;
    const close = () => mobile && setMobileOpen(false);
    return (
      <>
        <NavLink data-testid={TESTIDS.navDashboard} to="/dashboard" onClick={close}
          className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
          <LayoutDashboard className="w-4 h-4" /><span>Absensi</span>
        </NavLink>
        <NavLink data-testid={TESTIDS.navHistory} to="/riwayat" onClick={close}
          className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
          <History className="w-4 h-4" /><span>Riwayat</span>
        </NavLink>
        <NavLink data-testid={TESTIDS.navLeave} to="/cuti" onClick={close}
          className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
          <PlaneTakeoff className="w-4 h-4" /><span>Cuti & Izin</span>
        </NavLink>
        <NavLink data-testid={TESTIDS.navOvertime} to="/lembur" onClick={close}
          className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
          <Clock3 className="w-4 h-4" /><span>Lembur</span>
        </NavLink>
        {user?.role !== "staff" && (
          <NavLink data-testid={TESTIDS.navApprovals} to="/persetujuan" onClick={close}
            className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
            <CheckSquare className="w-4 h-4" /><span>Persetujuan</span>
          </NavLink>
        )}
        {isAdmin && (
          <>
            {!mobile && <div className="w-px h-6 bg-black/10 mx-2" />}
            {mobile && <div className="border-t border-black/10 my-2 px-4 pt-3 pb-1 label-tiny text-gray-500">Admin</div>}
            <NavLink data-testid={TESTIDS.navAdminDashboard} to="/admin" end onClick={close}
              className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
              <LayoutDashboard className="w-4 h-4" /><span>Admin</span>
            </NavLink>
            <NavLink data-testid={TESTIDS.navAdminUsers} to="/admin/users" onClick={close}
              className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
              <Users className="w-4 h-4" /><span>Karyawan</span>
            </NavLink>
            {user?.role === "admin" && (
              <>
                <NavLink data-testid={TESTIDS.navAdminOffices} to="/admin/kantor" onClick={close}
                  className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
                  <Building2 className="w-4 h-4" /><span>Kantor</span>
                </NavLink>
                <NavLink data-testid={TESTIDS.navAdminUnits} to="/admin/unit-kerja" onClick={close}
                  className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
                  <Landmark className="w-4 h-4" /><span>Unit Kerja</span>
                </NavLink>
              </>
            )}
            <NavLink data-testid={TESTIDS.navAdminReports} to="/admin/laporan" onClick={close}
              className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
              <FileBarChart2 className="w-4 h-4" /><span>Laporan</span>
            </NavLink>
            {user?.role === "admin" && (
              <NavLink data-testid={TESTIDS.navAdminSettings} to="/admin/pengaturan" onClick={close}
                className={({ isActive }) => `${base} ${isActive ? active : ""}`}>
                <Settings2 className="w-4 h-4" /><span>Setting</span>
              </NavLink>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div data-testid={TESTIDS.appShell} className="min-h-screen bg-white text-black flex flex-col">
      <header className="border-b border-black/10 bg-white sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-10 h-16 gap-3">
          <div data-testid={TESTIDS.navLogo}
            className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0"
            onClick={() => navigate("/dashboard")}>
            <div className="w-8 h-8 bg-klein" />
            <div className="font-display font-bold tracking-tight text-base sm:text-lg">ABSENSI-EPISI</div>
            <span className="hidden sm:inline-flex ml-2 items-center gap-1 border border-black/20 px-2 py-0.5 text-[10px] uppercase tracking-widest">
              {isAdmin ? <ShieldCheck className="w-3 h-3" /> : null}
              {ROLES[user?.role] || user?.role}
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 overflow-x-auto">
            <NavItems />
          </nav>

          <div data-testid={TESTIDS.navUserMenu} className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold leading-tight truncate max-w-[160px]">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate max-w-[160px]">{user?.email}</div>
            </div>
            <div className="w-9 h-9 bg-black text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
              {user?.name?.[0] || "U"}
            </div>
            <button data-testid={TESTIDS.navChangePassword} onClick={() => navigate("/ubah-password")}
              title="Ubah Password"
              className="hidden sm:inline-flex border border-black/20 hover:bg-black hover:text-white p-2 transition-colors">
              <KeyRound className="w-4 h-4" />
            </button>
            <button data-testid={TESTIDS.navLogout} onClick={logout} title="Keluar"
              className="border border-black/20 hover:bg-black hover:text-white p-2 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
            {/* Hamburger */}
            <button data-testid="nav-mobile-toggle" onClick={() => setMobileOpen(true)}
              className="lg:hidden border border-black/20 hover:bg-black hover:text-white p-2 transition-colors">
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative ml-auto w-72 max-w-[85vw] bg-white h-full overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 border-b border-black/10">
              <div className="font-display font-bold tracking-tight">ABSENSI-EPISI</div>
              <button onClick={() => setMobileOpen(false)} className="p-2 hover:bg-black hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-black/10">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-xs text-gray-500">{user?.email}</div>
              <span className="mt-2 inline-flex items-center gap-1 border border-black/20 px-2 py-0.5 text-[10px] uppercase tracking-widest">
                {isAdmin ? <ShieldCheck className="w-3 h-3" /> : null}
                {ROLES[user?.role] || user?.role}
              </span>
            </div>
            <nav className="flex flex-col py-2 flex-1">
              <NavItems mobile />
            </nav>
            <div className="border-t border-black/10 p-4 space-y-2">
              <button onClick={() => { setMobileOpen(false); navigate("/ubah-password"); }}
                className="w-full inline-flex items-center justify-center gap-2 border border-black/20 py-2 text-sm">
                <KeyRound className="w-4 h-4" /> Ubah Password
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 flex-1">{children}</main>

      <footer className="border-t border-black/10 mt-16 px-4 sm:px-6 lg:px-10 py-6 text-xs text-gray-500 flex items-center justify-between">
        <div>© {new Date().getFullYear()} Absensi-EPISI · @agung_gumilar</div>
        <div className="label-tiny">v1.2</div>
      </footer>
    </div>
  );
}
